import * as THREE from 'three';
import EventDispatcher from '../common/EventDispatcher';
import { gsap, Expo, Cubic, Power0 } from 'gsap';
import { Global } from '../common/global';
import { isMobile } from 'mobile-device-detect';

const INITIAL_CAMERA_POSITION = new THREE.Vector3(
  !isMobile ? -1.2 : -1.05,
  5,
  isMobile ? -11 : -10.5
);
const INITIAL_LOOK_AT_POSITION = new THREE.Vector3(
  !isMobile ? -1.2 : -1.05,
  0,
  -3
);

const CAMERA_FOLLOW_LERP_X = 0.5;
const CAMERA_FOLLOW_LERP_Y = 0.1;
const CAMERA_FOLLOW_LERP_Z = 0.5;
const CAMERA_LOOKAT_Y_PADD = 1.5;
const DEFAULT_SHAKE_VALUE = {
  value: 0.0,
};

export default class Camera extends THREE.PerspectiveCamera {
  skyLookFactor = new THREE.Vector3(0, 2, -5);
  lerpZReduce = 0;
  lastCameraPosition = INITIAL_CAMERA_POSITION.clone();
  gameStarted = false;

  constructor(fov, ratio, near, far) {
    super(fov, ratio, near, far);

    this.onEvents = this.onEvents.bind(this);
  }

  init() {
    this.initPanCompleted = false;
    this.panY = {
      value: 0,
    };
    this.camerastart = null;
    this.canShakeCamera = false;
    this._lookTarget = null; // initially (0,0,0) or set to current ball pos
    this._lookSmoothing = isMobile ? 0.4 : 0.05; // 0..1, smaller = smoother/slower
    this.lookAtSpeed = {
      value: 0,
    };
    this.zoomFact = {
      value: 0,
    };
    this.events = EventDispatcher.getObj();

    this.events.addEventListener('CAMERA_EVENTS', this.onEvents);
    this.events.addEventListener('GAMESCENE_EVENTS', this.onEvents);
    this.events.addEventListener('PLAYER_EVENTS', this.onEvents);
    this.events.addEventListener('GAME_EVENTS', this.onEvents);

    // this.position.copy(INITIAL_CAMERA_POSITION);
    this.initCameraFixed = false;
    this.shakeVal = DEFAULT_SHAKE_VALUE;
    // this.lookAt(INITIAL_LOOK_AT_POSITION);

    this.panSpeed = {
      value: 0,
    };
  }
  prepareToMoveCamera() {
    this._lookTarget = null;
    this.lookAtSpeed = {
      value: 0,
    };
    if (this.camerastart == null) this.camerastart = this.position.clone();
  }
  onEvents(data) {
    switch (data['message']['event_type']) {
      case 'reset_game':
        this.reset();
        break;
      case 'resize':
        this.onResize(data['message']['data']);
        break;
      case 'update':
        this.update();
        break;
      case 'move_camera':
        this.smoothFollowBall(data['message']['data']);
        break;
      case 'move_camera_init':
        this.prepareToMoveCamera();
        break;
      case 'shake_camera':
        this.prepareToShake(data['message']['data']);
        break;
      case 'start_game':
        this.gameStarted = true;
        this.initCameraFixed = false;
        break;
      case 'camera_pan_start':
        this.updatePanSpeed();
        break;
      case 'game_over':
        // this.onLevelCompleted();
        break;
      case 'reset_camera':
        this.resetCamera();
        break;
      case 'assign_position':
        this.zoomOut();
        break;
      case 'set_camera_view':
        this.setCameraView(data['message']['data']);
        break;
      case 'init_scene':
        this._lookTarget = null;
        this.lookAtData = null;
        break;
      case 'add_intro_view':
        this.addIntroView();
        break;
      // case 'dispose':
      //   this.dispose();
      //   break;
    }
  }
  addIntroView() {
    this.position.set(6, 3, -7);
    // this.rotation.y = Math.PI * 0.01;

    // return;

    this.tempLookAt = this.keeperPosition.clone();
    this.introTwn = gsap.to(this.position, {
      duration: Global.cameraPanTime,
      x: -0.85,
      y: this.initTargetPos.y,
      z: this.initTargetPos.z,
      ease: 'cubic.inOut',
      onUpdate: (v) => {
        const t = this.introTwn.progress();
        const lookAtPos = new THREE.Vector3(-0.85, 0, -1.2);

        this.lookAt(
          lookAtPos
            .clone()
            .lerp(new THREE.Vector3(-0.95, 1.3, -15), Math.pow(t, 4))
        );
      },
      onComplete: () => {
        this.initPanCompleted = true;
        this.lookAtData = null;
        this.events.dispatchEvent({
          type: 'GAMESCENE_EVENTS',
          message: {
            event_type: 'start_game',
            data: {},
          },
        });
      },
    });
  }
  updatePanSpeed() {
    gsap.to(this.panSpeed, {
      value: 0.0005,
      yoyo: true,
      repeat: 1,
      duration: 0.5,
    });
  }
  zoomIn() {
    gsap.killTweensOf(this.zoomFact);
    gsap.to(this.zoomFact, {
      value: 1,
      ease: Cubic.easeInOut,
      onUpdate: this.onZoomUpdate.bind(this),
      duration: isMobile ? 1 : 2,
    });
  }
  onZoomUpdate() {
    this.position.z =
      this.camerastart.z +
      (isMobile ? this.zoomFact.value : this.zoomFact.value / 3);
  }
  zoomOut() {
    gsap.killTweensOf(this.zoomFact);
    gsap.to(this.zoomFact, {
      value: 0,
      ease: Cubic.easeInOut,
      onUpdate: this.onZoomUpdate.bind(this),
      duration: 0.15,
    });
  }
  setCameraView(data) {
    const distanceBehindPlayer = 1.8 + 0;
    const cameraHeight = 1.5; //2.3;
    this.keeperPosition = data['position'];
    const playerRotationY = data['rotationY'];
    const lookAtDistance = 4;

    this.cameraPosition = new THREE.Vector3()
      .copy(this.keeperPosition)
      .addScaledVector(data['direction'], -distanceBehindPlayer);
    this.cameraPosition.y += cameraHeight;
    this.position.copy(this.cameraPosition);
    this.position.x = -0.85;

    this.initTargetPos = this.cameraPosition.clone();
  }

  reset() {
    this.initCameraFixed = false;
  }

  onResize(data) {
    this.aspect = data.aspect;
    this.updateProjectionMatrix();
  }

  update() {
    // this.rotation.y -= 0.001;
    // Apply camera shake effect
    // if (this.shakeVal.value > 0) {
    //   this.position.x += (Math.random() - 0.5) * this.shakeVal.value;
    //   this.position.y += (Math.random() - 0.5) * this.shakeVal.value;
    //   this.position.z += (Math.random() - 0.5) * this.shakeVal.value;
    // }

    Global.cameraPos = this.position.clone();
    if (this.canShakeCamera) {
      const elapsed = this.cameraclock.getElapsedTime();
      if (elapsed < 0.5) {
        const shake =
          Math.sin(elapsed * 100) *
          this.shakeIntensity.value *
          (1 - elapsed / 1);
        this.position.x = this.camerastart.x + shake;
        this.position.y = this.camerastart.y + shake * 0.5;
        this.position.z =
          this.camerastart.z +
          shake * 0.5 +
          (isMobile ? this.zoomFact.value : this.zoomFact.value / 3);
      } else {
        this.position.copy(this.camerastart);
        this.position.z += isMobile
          ? this.zoomFact.value
          : this.zoomFact.value / 3;
      }
    }
    if (this.lookAtData) {
      if (this._lookTarget == null) {
        this._lookTarget = this.lookAtData.position;
      }
      this._lookTarget.lerp(
        this.lookAtData.position,
        Math.min(1, this._lookSmoothing * this.lookAtSpeed.value * Global.delta)
      );
      this.initPanCompleted && this.lookAt(this._lookTarget);
    }
  }

  smoothFollowBall(data) {
    // if (Global.isJumpInProgress && !data.byForce) return;
    if (data.reset) {
      // return;
      this.lookAtSpeed.value = isMobile ? 1 : 2;
    } else {
      this.lookAtSpeed.value = 0;

      gsap.to(this.lookAtSpeed, {
        value: 1,
        ease: Cubic.easeIn,
        delay: 0.1,
        // yoyo: true,
        onUpdate: function () {},
        duration: isMobile ? 0.3 : 0.3,
      });
      this.zoomIn();
    }

    this._lookSmoothing =
      (isMobile ? 0.08 : 0.03) *
      (Global.jumpHeight == 'high'
        ? 1
        : Global.jumpHeight == 'mid'
        ? 0.8
        : 0.6);

    this.lookAtData = data;
  }

  prepareToShake(data) {
    if (Global.gameOver) return false;

    this.cameraclock = new THREE.Clock();
    this.canShakeCamera = true;
    this.shakeIntensity = {
      value: 0.2,
    };
    gsap.to(this.shakeIntensity, {
      duration: 0.5,
      value: 0,
    });

    /*  this.shakeVal = {
      value: data['shake_fact'] * 0.25,
    };

    gsap.to(this.shakeVal, {
      duration: data['shake_time'] / 10,
      delay: 0,
      value: 0,
      yoyo: true,
      repeat: 20,
      onUpdate: function () {}.bind(this),
    }); */
  }

  resetCamera() {
    this.initCameraFixed = false;
    this.lastCameraPosition = INITIAL_CAMERA_POSITION.clone();
    this.skyLookFactor = new THREE.Vector3(0, 2, -5);
    this.lerpZReduce = 0;
    // document.querySelector(".tint").classList.remove('active');
  }
  dispose() {
    if (this.events) {
      this.events.removeEventListener('CAMERA_EVENTS', this.onEvents);
      this.events.removeEventListener('GAMESCENE_EVENTS', this.onEvents);
      this.events.removeEventListener('PLAYER_EVENTS', this.onEvents);
      this.events.removeEventListener('GAME_EVENTS', this.onEvents);
    }
  }
}
